/**
 * @module features/files/file-item
 * @description Component for rendering individual file items with thumbnails and actions
 */

import { getFileIcon, getContentTypeName, inferContentTypeFromFilename } from './file-utils.js';
import { formatFileSize, formatDate } from '../../utils/formatters.js';
import { escapeHtml } from '../../utils/html-utils.js';
import { viewFile, downloadFile, deleteFile } from './file-operations.js';
import { canDeleteFiles } from '../../core/auth/auth-validators.js';
import { getDOMElements } from '../../ui/dom/dom-manager.js';
import { logger } from '../../utils/logger.js';

/**
 * Create a file item element with thumbnails, metadata, and action buttons
 * @param {Object} file - File object with metadata
 * @param {Object} user - Firebase user object
 * @param {boolean} highlight - Whether to highlight this file item
 * @param {string} entityType - Entity type (projects, opportunities, leads, contacts)
 * @param {string|null} entityId - Entity ID for operations
 * @returns {Promise<HTMLElement>} File item element (async due to permission check)
 */
export async function createFileItem(file, user, highlight = false, entityType = 'projects', entityId = null) {
    const div = document.createElement('div');
    div.className = highlight ? 'file-item file-item-highlight' : 'file-item';
    const archivedFileObjectId = file.archivedFileObjectId;
    div.setAttribute('data-file-id', archivedFileObjectId);

    const fileName = file.name;
    const fileSize = file.size;
    const icon = getFileIcon(fileName);
    const size = formatFileSize(fileSize);

    // If contentType is missing or is the generic 'application/octet-stream', try to infer it from the filename
    let contentType = file.contentType;
    const isGenericType = contentType === 'application/octet-stream';

    if (!contentType || isGenericType) {
        const inferredType = inferContentTypeFromFilename(fileName);
        if (inferredType) {
            logger.debug(`🔍 File "${fileName}": Inferred type ${inferredType} from filename`);
            contentType = inferredType;
        } else if (!fileName.includes('.')) {
            // Only log if debugging - file has no extension (common in this system)
            logger.debug(`ℹ️ File "${fileName}" has no extension, will determine type when viewed`);
        }
    }

    const contentTypeName = getContentTypeName(contentType);

    // WORKAROUND: Since filenames often have no extensions and API returns generic types,
    // we can't reliably determine if a file is viewable from the list.
    // Always show "View" button - the viewFile() function will intelligently
    // fetch the real contentType and either view or show an informative message.
    const showViewButton = true; // Always show - function handles the logic

    // Check delete permissions using Firebase Custom Claims (async)
    const userCanDelete = await canDeleteFiles();

    // Generate Google Maps link if coordinates are available (camelCase only)
    let mapLink = '';
    const latitude = file.latitude;
    const longitude = file.longitude;
    if (latitude != null && longitude != null) {
        const lat = Number(latitude).toFixed(6);
        const lng = Number(longitude).toFixed(6);
        const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        mapLink = ` (<a href="${mapUrl}" target="_blank" class="map-link" title="View location on Google Maps">map</a>)`;
    }

    // Determine if we should show a thumbnail or icon (camelCase only)
    const hasThumbnail = file.hasThumbnail;
    const thumbnailUrl = file.thumbnailUrl;
    const thumbnailHtml = hasThumbnail && thumbnailUrl
        ? `<div class="file-thumbnail"><img src="${escapeHtml(thumbnailUrl)}" alt="${escapeHtml(fileName)}" loading="lazy" onerror="this.parentElement.innerHTML='<div class=\\'file-icon\\'>${icon}</div>'"></div>`
        : `<div class="file-icon">${icon}</div>`;

    const dateAdded = file.dateAdded;
    const category = file.category;

    // Build tooltip with size and date information
    const tooltipParts = [];
    if (size) tooltipParts.push(`Size: ${size}`);
    if (dateAdded) tooltipParts.push(`Added: ${formatDate(dateAdded)}`);
    if (contentType && contentTypeName !== 'Unknown') tooltipParts.push(`Type: ${contentTypeName}`);
    const tooltip = tooltipParts.join(' • ');

    div.innerHTML = `
        ${thumbnailHtml}
        <div class="file-info">
            <div class="file-name" title="${escapeHtml(tooltip)}">${escapeHtml(fileName)}${mapLink}</div>
            ${category ? `<div class="file-category-badge">${escapeHtml(category)}</div>` : ''}
        </div>
        <div class="file-actions">
            ${showViewButton ? `<button class="btn btn-primary btn-small view-btn" data-archived-file-object-id="${archivedFileObjectId}">View</button>` : ''}
            <button class="btn btn-secondary btn-small download-btn" data-archived-file-object-id="${archivedFileObjectId}" data-name="${escapeHtml(fileName)}">Download</button>
            ${userCanDelete ? `<button class="btn btn-danger btn-small delete-btn" data-archived-file-object-id="${archivedFileObjectId}" data-name="${escapeHtml(fileName)}" title="Delete this file permanently">Delete</button>` : ''}
        </div>
    `;

    // Add event listeners
    const viewBtn = div.querySelector('.view-btn');
    const downloadBtn = div.querySelector('.download-btn');
    const deleteBtn = div.querySelector('.delete-btn');

    if (viewBtn) {
        viewBtn.addEventListener('click', async () => {
            // Prevent multiple clicks
            if (viewBtn.disabled) return;

            // Disable button and show loading state
            viewBtn.disabled = true;
            const originalText = viewBtn.textContent;
            viewBtn.textContent = '⏳ Loading...';

            try {
                await viewFile(viewBtn.dataset.archivedFileObjectId, entityType);
            } finally {
                // Re-enable button and restore original text
                viewBtn.disabled = false;
                viewBtn.textContent = originalText;
            }
        });
    }

    if (downloadBtn) {
        downloadBtn.addEventListener('click', async () => {
            // Prevent multiple clicks
            if (downloadBtn.disabled) return;

            // Disable button and show loading state
            downloadBtn.disabled = true;
            const originalText = downloadBtn.textContent;
            downloadBtn.textContent = '⏳ Downloading...';

            try {
                await downloadFile(downloadBtn.dataset.archivedFileObjectId, downloadBtn.dataset.name, entityType);
            } finally {
                // Re-enable button and restore original text
                downloadBtn.disabled = false;
                downloadBtn.textContent = originalText;
            }
        });
    }

    if (deleteBtn) {
        const dom = getDOMElements;
        deleteBtn.addEventListener('click', () => deleteFile(deleteBtn.dataset.archivedFileObjectId, deleteBtn.dataset.name, user, entityType, entityId, dom.filesContainer));
    }

    return div;
}

// Default export
export default {
    createFileItem
};
